import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  categorizeTransaction,
  matchCategoryPhrase,
  type CategoryTreeNode,
  type FinanceTransaction,
  type PhraseResult,
} from '../api.js';
import { speechCtor, speechErrorText, transcriptOf, type SpeechRecognitionLike } from '../capture/speech.js';
import { Icon, Sheet } from '../ui/index.js';
import { dayLabel, isUncategorized, money } from './format.js';
import { useCategoryTree, type CategoryIndex, type FlatCategory } from './tree.js';

/**
 * The category picker: the tree, drilled one level at a time (Monarch-style
 * big tiles for the four roots, then lists), with search, the last few picks
 * and a mic for "it was a video game". A pick can also be remembered for the
 * merchant, which writes a rule server-side and files that merchant's other
 * uncategorized rows — the count comes back as `alsoFiled`.
 */

export interface PickResult {
  category: string;
  alsoFiled: number;
  transaction: FinanceTransaction;
}

const RECENT_KEY = 'vitals.money.recentCategories';
const RECENT_MAX = 6;

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(path: string): void {
  try {
    const next = [path, ...readRecents().filter((p) => p !== path)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Private mode / storage off: recents are a convenience, not state.
  }
}

/** Soft per-root tint for the level-one tiles. */
const ROOT_TINT: Record<string, string> = {
  essentials: '#6f9ef5',
  lifestyle: '#e78fc9',
  business: '#f4a462',
  money: '#8ea4c2',
  income: '#3dbb8a',
};
const ROOT_ICON: Record<string, Parameters<typeof Icon>[0]['name']> = {
  essentials: 'cart',
  lifestyle: 'star',
  business: 'folder',
  money: 'wallet',
  income: 'arrow-up',
};

export function CategoryPickerBody({
  txn,
  inline = false,
  onPicked,
  lead,
}: {
  txn: FinanceTransaction;
  /** Inline (Review): no transaction header — the card around it already shows one. */
  inline?: boolean;
  onPicked: (r: PickResult) => void;
  /** Rendered above the search (Review's suggestion chips), handed this picker's own `pick` so Remember applies. */
  lead?: (pick: (path: string) => void, busy: boolean) => ReactNode;
}): JSX.Element {
  const idx = useCategoryTree();
  const inflow = txn.amountCents > 0;
  const merchant = txn.merchant ?? txn.description;

  const [trail, setTrail] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // Review files new rows, where teaching a rule is the point. Re-filing one
  // row from the ledger is usually an exception, so it starts unticked there.
  const [remember, setRemember] = useState(inline);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recents] = useState(readRecents);

  // Voice / phrase
  const canSpeak = speechCtor() !== null;
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [said, setSaid] = useState('');
  const [phrase, setPhrase] = useState<PhraseResult | null>(null);
  const [phraseBusy, setPhraseBusy] = useState(false);
  const [phraseText, setPhraseText] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const rec = useRef<SpeechRecognitionLike | null>(null);
  const heard = useRef('');

  useEffect(() => () => rec.current?.abort(), []);

  /** Income only for money coming in; "uncategorized" is never a pick, only a reset. */
  const allowed = (path: string): boolean => {
    const root = path.split('.')[0];
    if (root === 'uncategorized') return false;
    if (root === 'income') return inflow;
    return true;
  };

  const roots = idx.tree.filter((r) => allowed(r.path));
  const current = trail ? idx.byPath.get(trail) : undefined;
  const crumbs = trail ? trail.split('.').map((_, i, parts) => parts.slice(0, i + 1).join('.')) : [];

  const results = useMemo(() => searchTree(idx, query).filter((f) => allowed(f.path)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idx, query, inflow]);

  const pick = async (path: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Clearing a category never teaches a rule.
      const res = await categorizeTransaction(txn.id, { category: path, remember: remember && path !== 'uncategorized' });
      if (path !== 'uncategorized') pushRecent(path);
      onPicked({ category: path, alsoFiled: res.alsoFiled, transaction: res.transaction });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const runPhrase = async (text: string): Promise<void> => {
    const t = text.trim();
    if (!t) return;
    setSaid(t);
    setPhrase(null);
    setPhraseBusy(true);
    setNote(null);
    try {
      const res = await matchCategoryPhrase(t, txn.id);
      setPhrase({ ...res, matches: res.matches.filter((m) => allowed(m.path)) });
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Could not match that');
    } finally {
      setPhraseBusy(false);
    }
  };

  const listen = (): void => {
    const Ctor = speechCtor();
    if (!Ctor) return;
    if (rec.current) {
      rec.current.stop();
      return;
    }
    setNote(null);
    setPhrase(null);
    setSaid('');
    setInterim('');
    heard.current = '';
    let r: SpeechRecognitionLike;
    try {
      r = new Ctor();
    } catch {
      setNote(speechErrorText('unknown'));
      return;
    }
    r.lang = navigator.language || 'en-US';
    r.continuous = false;
    r.interimResults = true;
    r.onresult = (e) => {
      if (rec.current !== r) return;
      const t = transcriptOf(e);
      if (t.final) heard.current = t.final;
      setInterim([t.final, t.interim].filter(Boolean).join(' '));
    };
    r.onerror = (e) => {
      if (rec.current !== r || e.error === 'aborted') return;
      setNote(speechErrorText(e.error));
    };
    r.onend = () => {
      if (rec.current !== r) return;
      rec.current = null;
      setListening(false);
      setInterim('');
      if (heard.current) void runPhrase(heard.current);
    };
    rec.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      rec.current = null;
      setNote(speechErrorText('unknown'));
    }
  };

  const optionLabel = (path: string, fallback?: string): string =>
    idx.byPath.has(path) ? idx.full(path) : (fallback ?? idx.full(path));

  return (
    <div className={`mn-picker${inline ? ' mn-picker--inline' : ''}`} aria-busy={busy || undefined}>
      {!inline && (
        // Focus lands here, not on the search box, so a phone doesn't open its keyboard over the tiles.
        <div className="mn-txn" tabIndex={-1} data-autofocus>
          <span className="mn-txn__name">{merchant}</span>
          <span className={`mn-txn__amt num${inflow ? ' hb-amount--in' : ''}`}>{money(txn.amountCents, { sign: inflow })}</span>
          <span className="mn-txn__meta">
            {dayLabel(txn.postedAt.slice(0, 10))}
            {!isUncategorized(txn.category) ? ` · now ${idx.full(txn.category)}` : ''}
          </span>
        </div>
      )}

      {lead?.((path) => void pick(path), busy)}

      <div className="mn-picker__find">
        <label className="mn-search mn-picker__search">
          <Icon name="search" size={18} />
          <input
            type="search"
            placeholder="Search categories"
            aria-label="Search categories"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        {canSpeak && (
          <button
            type="button"
            className={`mn-picker__mic${listening ? ' mn-picker__mic--on' : ''}`}
            aria-label={listening ? 'Stop listening' : 'Say the category'}
            aria-pressed={listening}
            onClick={listen}
          >
            <Icon name={listening ? 'stop' : 'mic'} size={20} />
          </button>
        )}
      </div>

      <form
        className="mn-picker__phrase"
        onSubmit={(e) => {
          e.preventDefault();
          void runPhrase(phraseText);
        }}
      >
        <input
          value={phraseText}
          onChange={(e) => setPhraseText(e.target.value)}
          placeholder="Or describe it — “video games”"
          aria-label="Describe it"
        />
        <button type="submit" className="mn-picker__go" disabled={!phraseText.trim() || phraseBusy}>
          Match
        </button>
      </form>

      {(listening || interim || said || note) && (
        <div className="mn-voice" aria-live="polite">
          {(listening || interim) && <p className="mn-voice__heard">{interim || 'Listening…'}</p>}
          {!listening && !interim && said && <p className="mn-voice__heard">“{said}”</p>}
          {phraseBusy && <p className="mn-voice__note">Matching…</p>}
          {note && <p className="mn-voice__note">{note}</p>}
          {phrase && phrase.matches.length === 0 && <p className="mn-voice__note">No match — pick below</p>}
          {phrase && phrase.matches.length > 0 && (
            <>
              <div className="mn-voice__matches">
                {phrase.matches.map((m) => (
                  <button key={m.path} type="button" className="mn-voice__match" disabled={busy} onClick={() => void pick(m.path)}>
                    <Icon name="check" size={16} />
                    {optionLabel(m.path, m.label)}
                  </button>
                ))}
              </div>
              {phrase.source === 'ai' && <p className="mn-voice__note">suggested by Claude</p>}
            </>
          )}
        </div>
      )}

      {query.trim() ? (
        results.length === 0 ? (
          <p className="mn-picker__none">No category called “{query.trim()}”.</p>
        ) : (
          <ul className="ui-group mn-picks" aria-label="Matching categories">
            {results.map((f) => (
              <PickRow key={f.path} label={f.chain.join(' › ')} on={txn.category === f.path} onClick={() => void pick(f.path)} />
            ))}
          </ul>
        )
      ) : (
        <>
          {!trail && recents.some((p) => idx.byPath.has(p) && allowed(p)) && (
            <div className="mn-recents" aria-label="Recent categories">
              <span className="mn-recents__label">Recent</span>
              {recents
                .filter((p) => idx.byPath.has(p) && allowed(p))
                .map((p) => (
                  <button key={p} type="button" className="lo-chip mn-recents__chip" title={idx.full(p)} onClick={() => void pick(p)}>
                    {idx.leaf(p)}
                  </button>
                ))}
            </div>
          )}

          {!trail ? (
            <div className="mn-tiles">
              {roots.map((r) => (
                <button
                  key={r.path}
                  type="button"
                  className="mn-tile"
                  style={{ ['--tile' as string]: ROOT_TINT[r.key] ?? '#8ea4c2' }}
                  onClick={() => setTrail(r.path)}
                >
                  <span className="mn-tile__icon" aria-hidden="true">
                    <Icon name={ROOT_ICON[r.key] ?? 'grid'} size={20} />
                  </span>
                  <span className="mn-tile__label">{r.label}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              <nav className="mn-crumbs" aria-label="Category path">
                <button
                  type="button"
                  className="mn-crumbs__back"
                  aria-label="Back"
                  onClick={() => setTrail(crumbs.length > 1 ? crumbs[crumbs.length - 2]! : null)}
                >
                  <Icon name="chevron-left" size={18} />
                </button>
                <button type="button" className="mn-crumbs__link" onClick={() => setTrail(null)}>
                  All
                </button>
                {crumbs.map((c, i) => (
                  <span key={c} className="mn-crumbs__part">
                    <span aria-hidden="true">›</span>
                    {i === crumbs.length - 1 ? (
                      <span className="mn-crumbs__here" aria-current="page">
                        {idx.leaf(c)}
                      </span>
                    ) : (
                      <button type="button" className="mn-crumbs__link" onClick={() => setTrail(c)}>
                        {idx.leaf(c)}
                      </button>
                    )}
                  </span>
                ))}
              </nav>
              {current && (
                <ul className="ui-group mn-picks" aria-label={current.label}>
                  <PickRow label={`Just ${current.label}`} on={txn.category === current.path} strong onClick={() => void pick(current.path)} />
                  {(current.node.children ?? []).map((c: CategoryTreeNode) => (
                    <PickRow
                      key={c.path}
                      label={c.label}
                      on={txn.category === c.path}
                      drill={Boolean(c.children?.length)}
                      onClick={() => (c.children?.length ? setTrail(c.path) : void pick(c.path))}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}

      <label className="mn-remember">
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
        <span>Remember for {merchant}</span>
      </label>

      {error && (
        <p className="hb-form__error" role="alert">
          {error}
        </p>
      )}

      {!isUncategorized(txn.category) && (
        <button type="button" className="mn-picker__reset" disabled={busy} onClick={() => void pick('uncategorized')}>
          Clear category
        </button>
      )}
    </div>
  );
}

function PickRow({
  label,
  on,
  drill = false,
  strong = false,
  onClick,
}: {
  label: string;
  on: boolean;
  drill?: boolean;
  strong?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <li className="ui-swipe">
      <button type="button" className={`ui-pick${strong ? ' mn-pick--just' : ''}`} aria-current={on || undefined} onClick={onClick}>
        <span className="ui-pick__label">{label}</span>
        {on && <Icon name="check" size={20} className="ui-pick__check" />}
        {drill && <Icon name="chevron-right" size={18} className="ui-row__chev" />}
      </button>
    </li>
  );
}

/** Every node whose own label contains the query; prefix hits first, shallow before deep. */
export function searchTree(idx: CategoryIndex, query: string): FlatCategory[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return idx.flat
    .filter((f) => f.label.toLowerCase().includes(q))
    .sort((a, b) => {
      const ap = a.label.toLowerCase().startsWith(q) ? 0 : 1;
      const bp = b.label.toLowerCase().startsWith(q) ? 0 : 1;
      return ap - bp || a.chain.length - b.chain.length;
    })
    .slice(0, 25);
}

/** The picker in a sheet (the ledger's tap-to-recategorize). */
export default function CategoryPicker({
  txn,
  onClose,
  onPicked,
}: {
  txn: FinanceTransaction;
  onClose: () => void;
  onPicked: (r: PickResult) => void;
}): JSX.Element {
  return (
    <Sheet title="Category" onClose={onClose} className="mn-pickersheet">
      <CategoryPickerBody txn={txn} onPicked={onPicked} />
    </Sheet>
  );
}
